import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { Users, Building2, Tag, Sparkles, Calendar, FileText, Award, TrendingUp, ShieldAlert, FileDown } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);

    useEffect(() => { api.get('/admin/stats').then(({ data }) => setStats(data)); }, []);

    return (
        <AdminLayout>
            <div className="p-10" data-testid="admin-dashboard-page">
                <div className="mb-10 flex items-start justify-between gap-6">
                    <div>
                        <div className="overline">Overview</div>
                        <h1 className="font-serif text-4xl text-navy mt-2">Command Center</h1>
                        <div className="text-sm text-text-secondary mt-1">Real-time across all Inorbit malls</div>
                    </div>
                    <a
                        href="/inorbit-one-proposal.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline inline-flex items-center gap-2 whitespace-nowrap"
                        data-testid="download-proposal-btn"
                    >
                        <FileDown size={14} /> Strategic Brief (PDF)
                    </a>
                </div>

                {!stats ? <div>Loading…</div> : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="kpi-cards">
                            <KPI label="Customers" value={stats.customers.toLocaleString()} icon={Users} />
                            <KPI label="Malls" value={stats.malls} icon={Building2} />
                            <KPI label="Brands" value={stats.brands} icon={Tag} />
                            <KPI label="Live Offers" value={stats.offers} icon={Sparkles} />
                            <KPI label="Events" value={stats.events} icon={Calendar} />
                            <KPI label="Total Bills" value={stats.bills.total} icon={FileText} />
                            <KPI label="Pending Review" value={stats.bills.pending} icon={ShieldAlert} highlight={stats.bills.pending > 0} />
                            <KPI label="Redemptions" value={stats.redemptions} icon={Award} />
                        </div>

                        <div className="grid md:grid-cols-2 gap-6 mb-10">
                            <div className="bg-white border border-border-subtle p-8" data-testid="loyalty-summary">
                                <div className="overline mb-4">Loyalty Liability</div>
                                <div className="font-serif text-5xl text-navy">
                                    {stats.loyalty.total_points_issued.toLocaleString()} <span className="text-gold text-2xl">pts</span>
                                </div>
                                <div className="text-sm text-text-secondary mt-2">Total points issued lifetime</div>
                                <div className="mt-6 pt-6 border-t border-border-subtle">
                                    <div className="overline mb-2">Captured Spend</div>
                                    <div className="font-serif text-3xl text-navy">
                                        ₹{Number(stats.loyalty.total_captured_spend || 0).toLocaleString('en-IN')}
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white border border-border-subtle p-8" data-testid="tier-breakdown">
                                <div className="overline mb-4">Tier Distribution</div>
                                <div className="space-y-4">
                                    {['silver', 'gold', 'platinum', 'black'].map((t) => {
                                        const count = stats.tiers[t] || 0;
                                        const total = Object.values(stats.tiers).reduce((a, b) => a + b, 0) || 1;
                                        return (
                                            <div key={t}>
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="capitalize text-navy">{t}</span>
                                                    <span className="text-text-tertiary">{count}</span>
                                                </div>
                                                <div className="h-1.5 bg-stone">
                                                    <div className={`h-full tier-${t}`} style={{ width: `${count / total * 100}%` }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6" data-testid="bill-status-breakdown">
                            <div className="bg-white border border-border-subtle p-6">
                                <div className="overline text-emerald-700 mb-2">Approved Bills</div>
                                <div className="font-serif text-4xl text-navy">{stats.bills.approved}</div>
                            </div>
                            <div className="bg-white border border-border-subtle p-6">
                                <div className="overline text-amber-700 mb-2">Pending Review</div>
                                <div className="font-serif text-4xl text-navy">{stats.bills.pending}</div>
                                {stats.bills.pending > 0 && <Link to="/admin/bills" className="text-xs text-gold mt-2 inline-block" data-testid="goto-bill-queue">Review now →</Link>}
                            </div>
                            <div className="bg-white border border-border-subtle p-6">
                                <div className="overline text-rose-700 mb-2">Fraud Review</div>
                                <div className="font-serif text-4xl text-navy">{stats.bills.fraud}</div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </AdminLayout>
    );
}

function KPI({ label, value, icon: Icon, highlight }) {
    return (
        <div className={`bg-white p-6 border ${highlight ? 'border-gold' : 'border-border-subtle'}`}>
            <Icon size={18} strokeWidth={1.3} className={highlight ? 'text-gold' : 'text-text-tertiary'} />
            <div className="font-serif text-3xl text-navy mt-3">{value}</div>
            <div className="text-xs uppercase tracking-widest text-text-tertiary mt-1">{label}</div>
        </div>
    );
}
